import { useState, useEffect } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, ArrowRight, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api` });
const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await publicApi.post("/auth/password-reset/request", { email }); setSent(true); }
    finally { setBusy(false); }
  };
  return (
    <Shell>
      {sent ? (
        <div data-testid="reset-sent" className="text-center space-y-3">
          <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto" />
          <h1 className="font-display text-3xl">Check your email</h1>
          <p className="text-zinc-400 text-sm">If an account exists for <span className="text-white">{email}</span>, we&apos;ve sent a reset link. The link expires in 30 minutes.</p>
          <Link to="/login" className="inline-block mt-4 text-sm text-zinc-500 hover:text-white">Back to sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
          <h1 className="font-display text-3xl">Reset your password</h1>
          <p className="text-sm text-zinc-400">Enter the email on your StageCall account and we&apos;ll email you a reset link.</p>
          <div>
            <Label className="text-zinc-300 text-xs uppercase tracking-widest">Email</Label>
            <Input data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 bg-[#121212] border-white/10 h-12" />
          </div>
          <button data-testid="forgot-submit" disabled={busy} style={goldBtn} className="w-full h-11 rounded-md font-semibold inline-flex items-center justify-center gap-1">
            {busy ? "Sending…" : (<>Send reset link <ArrowRight className="w-4 h-4" /></>)}
          </button>
          <div className="text-center"><Link to="/login" className="text-xs text-zinc-500 hover:text-white">Back to sign in</Link></div>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const tk = token || sp.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (pw !== pw2) return setErr("Passwords don't match");
    if (pw.length < 8) return setErr("Password must be at least 8 characters");
    setBusy(true);
    try { await publicApi.post("/auth/password-reset/confirm", { token: tk, new_password: pw }); nav("/login?reset=1"); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not reset password"); }
    finally { setBusy(false); }
  };
  return (
    <Shell>
      <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
        <h1 className="font-display text-3xl">Choose a new password</h1>
        <div>
          <Label className="text-zinc-300 text-xs uppercase tracking-widest">New password</Label>
          <Input data-testid="reset-pw" type="password" required value={pw} onChange={(e) => setPw(e.target.value)} className="mt-2 bg-[#121212] border-white/10 h-12" />
        </div>
        <div>
          <Label className="text-zinc-300 text-xs uppercase tracking-widest">Confirm</Label>
          <Input data-testid="reset-pw2" type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)} className="mt-2 bg-[#121212] border-white/10 h-12" />
        </div>
        {err && <div data-testid="reset-err" className="text-sm text-red-400 border border-red-500/30 bg-red-500/10 rounded-md px-3 py-2">{err}</div>}
        <button data-testid="reset-submit" disabled={busy} style={goldBtn} className="w-full h-11 rounded-md font-semibold">{busy ? "Saving…" : "Save new password"}</button>
      </form>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6">
      <div className="w-full max-w-md space-y-6 animate-fade-up">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-gold" /><span className="font-display text-2xl">StageCall</span>
        </div>
        {children}
      </div>
    </div>
  );
}

export function LegalPage({ title, kind }) {
  const [text, setText] = useState(null);
  useEffect(() => {
    publicApi.get("/public/legal")
      .then((r) => setText((kind === "terms" ? r.data.terms : r.data.privacy) || ""))
      .catch(() => setText(""));
  }, [kind]);
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white px-6 py-16">
      <div className="max-w-2xl mx-auto space-y-5" data-testid={`legal-${kind}`}>
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-gold" /><span className="font-display text-2xl">StageCall</span>
        </div>
        <h1 className="font-display text-4xl">{title}</h1>
        {text === null ? (
          <p className="text-zinc-500 text-sm">Loading…</p>
        ) : text ? (
          <p className="text-zinc-300 text-sm whitespace-pre-wrap leading-relaxed" data-testid={`legal-${kind}-body`}>{text}</p>
        ) : (
          <p className="text-zinc-500 text-sm">This document has not been configured yet. Ask the platform administrator to publish it.</p>
        )}
        <Link to="/login" className="inline-block mt-6 text-sm text-zinc-500 hover:text-white">← Back</Link>
      </div>
    </div>
  );
}
